import React, { useEffect, useState } from "react";
import Label from "../FormComponents/Label";
import { clasStyles } from "../../utils/classStyles";
import Input from "../FormComponents/Input";
import { ImagePreview } from "./Preview/ElementsPreview";
import MaterialIcon from "./MaterialIcon";

const BasicStyleEditor = ({ fieldType, value, callbackFunction }) => {
  const [styleConditions, setStyleConditions] = useState({});

  const excludedTypes = ["html", "dynamic-html", "file_viewer"];
  useEffect(() => {
    const obj = {
      font_size: false,
      font_weight: false,
      font_decoration: false,
      alignment: false,
      border: false,
      border_radius: false,
      padding: false,
      margin: false,
      shadow: false,
      color: false,
      background_color: false,
      image_shape: false
    };

    switch (fieldType) {
      case "image":
        obj.image_shape = true;
        obj.padding = true;
        obj.margin = true;
        obj.shadow = true;
        obj.border = true;
        break;

      case "button":
      case "dropdown":
      case "button-group":
      case "dropdown-navigate":
        obj.size = true;
        obj.alignment = true;
        obj.type = true;
        break;

      default:
        obj.font_size = true;
        obj.font_weight = true;
        obj.font_decoration = true;
        obj.alignment = true;
        obj.border = true;
        obj.border_radius = true;
        obj.padding = true;
        obj.margin = true;
        obj.shadow = true;
        obj.color = true;
        obj.background_color = true;
        break;
    }
    setStyleConditions(obj);
  }, [fieldType]);

  if (excludedTypes.includes(fieldType)) return;

  return (
    <>
      <StyleEditor styleObj={styleConditions} fieldType={fieldType} value={value} callbackFunction={callbackFunction} />
    </>
  );
};

export default BasicStyleEditor;

export const StyleEditor = ({ styleObj, fieldType = "", value, callbackFunction }) => {
  const classCallback = (newClassData) => {
    const updatedValue = { ...value, classData: newClassData };
    callbackFunction(updatedValue);
  };
  const inlineCallback = (newInlineData) => {
    const updatedValue = { ...value, inlineData: newInlineData };
    callbackFunction(updatedValue);
  };
  return (
    <div className="position_relative">
      <div
        className="py_3 position_sticky top_0"
        /*style={{ backgroundColor: "#f1f3f4" }}*/ style={{ backgroundColor: "var(--builder-bg)" }}
      >
        <FieldStylePreview value={value} fieldType={fieldType} />
      </div>
      <div /*style={{ backgroundColor: "rgb(251 251 251)" }}*/ style={{ backgroundColor: "var(--builder-block-bg)" }}>
        <ClassNameStyles classData={value.classData} classCallback={classCallback} styleConditions={styleObj} />
        <InlineStyles inlineData={value.inlineData} inlineCallback={inlineCallback} styleConditions={styleObj} />
      </div>
    </div>
  );
};

const FieldStylePreview = ({ value, fieldType }) => {
  const classNames = Object.values(value.classData)
    .filter((val) => val)
    .join(" ");

  const typeClass = Object.values(value.classData).find((val) =>
    ["builder_btn_primary", "builder_btn_success", "builder_btn_delete", "icon_label"].includes(val)
  );

  const applyBgLight = !typeClass;

  return (
    <>
      {fieldType === "image" ? (
        <div
        // className={`bg_light  ${Object.values(value.classData)
        //   .filter((value) => value)
        //   .join(" ")}`}
        // style={value.inlineData}
        >
          <ImagePreview config={{ className: value.classData }} imageStyle={value.inlineData} />
        </div>
      ) : (
        <div className={`${applyBgLight ? "var(--builder-plain)" : ""} ${classNames}`} style={value.inlineData}>
          Preview
        </div>
      )}
    </>
  );
};

const ClassNameStyles = ({ classData, classCallback, styleConditions }) => {
  const handleUpdate = (key, value) => {
    let newValue = value;
    if (classData[key] === value) {
      newValue = "";
    }
    const updatedClassData = { ...classData, [key]: newValue };
    classCallback(updatedClassData);
  };

  return (
    <div>
      {styleConditions.font_size && (
        <FontSize value={classData["fontSize"]} onUpdate={(newValue) => handleUpdate("fontSize", newValue)} />
      )}
      {styleConditions.font_weight && (
        <FontWeight value={classData["fontWeight"]} onUpdate={(newValue) => handleUpdate("fontWeight", newValue)} />
      )}
      {styleConditions.font_decoration && (
        <FontDecoration
          value={classData["fontDecoration"]}
          onUpdate={(newValue) => handleUpdate("fontDecoration", newValue)}
        />
      )}
      {styleConditions.size && (
        <Size value={classData["size"]} onUpdate={(newValue) => handleUpdate("size", newValue)} />
      )}
      {styleConditions.alignment && (
        <Alignment value={classData["alignment"]} onUpdate={(newValue) => handleUpdate("alignment", newValue)} />
      )}
      {styleConditions.image_shape && (
        <ImageShape value={classData["imageShape"]} onUpdate={(newValue) => handleUpdate("imageShape", newValue)} />
      )}
      {styleConditions.border && (
        <Border value={classData["border"]} onUpdate={(newValue) => handleUpdate("border", newValue)} />
      )}
      {styleConditions.border_radius && (
        <BorderRadius
          value={classData["borderRadius"]}
          onUpdate={(newValue) => handleUpdate("borderRadius", newValue)}
        />
      )}
      {styleConditions.padding && (
        <Padding value={classData["padding"]} onUpdate={(newValue) => handleUpdate("padding", newValue)} />
      )}
      {styleConditions.margin && (
        <Margin value={classData["margin"]} onUpdate={(newValue) => handleUpdate("margin", newValue)} />
      )}
      {styleConditions.shadow && (
        <Shadow value={classData["shadow"]} onUpdate={(newValue) => handleUpdate("shadow", newValue)} />
      )}
      {styleConditions.type && (
        <Type value={classData["type"]} onUpdate={(newValue) => handleUpdate("type", newValue)} />
      )}
    </div>
  );
};

const InlineStyles = ({ inlineData, inlineCallback, styleConditions }) => {
  const handleUpdate = (key, value) => {
    let newValue = value;

    if (inlineData[key] === value) {
      newValue = "";
    }
    const updatedInlineData = { ...inlineData, [key]: newValue };
    inlineCallback(updatedInlineData);
  };
  return (
    <div>
      {styleConditions.background_color && (
        <BackgroundColor
          value={inlineData["backgroundColor"]}
          onUpdate={(newValue) => handleUpdate("backgroundColor", newValue)}
        />
      )}
      {styleConditions.color && (
        <Color value={inlineData["color"]} onUpdate={(newValue) => handleUpdate("color", newValue)} />
      )}
    </div>
  );
};

// Sub-components for each style key
const getClassName = (value, currentClass) => {
  let classString = "style_action_btn";
  if (value === currentClass) {
    classString += " selected_style_action_btn";
  }
  return classString;
};

const Alignment = ({ value, onUpdate }) => {
  const fontSizeOptions = clasStyles.alignment;

  return (
    <div className="single_style_wrapper">
      <Label>Alignment</Label>
      <div className="flex_row align_center">
        {fontSizeOptions.map((fontObj, index) => (
          <div key={index} className={getClassName(value, fontObj.class)} onClick={() => onUpdate(fontObj.class)}>
            <MaterialIcon name={fontObj.text} className="font_size_medium" />
          </div>
        ))}
      </div>
    </div>
  );
};

const Size = ({ value, onUpdate }) => {
  const sizeOptions = clasStyles.size;

  return (
    <div className="single_style_wrapper">
      <Label>Size</Label>
      <div className="flex_row align_center gap_2">
        {sizeOptions.map((sizeObj, index) => (
          <div
            key={index}
            className={getClassName(value, sizeObj.class)}
            onClick={() => onUpdate(sizeObj.class)}
            style={{ ...sizeObj.style }}
          >
            <span>{sizeObj.name}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

const Type = ({ value, onUpdate }) => {
  const typeOptions = clasStyles.type;

  return (
    <div className="single_style_wrapper">
      <Label>Type</Label>
      <div className="flex_wrap gap_3">
        {typeOptions.map((typeObj, index) => {
          const isSelected = value === typeObj.class;
          return (
            <div
              key={index}
              className={`${getClassName(value, typeObj.class)} style_action_btn ${
                isSelected ? "selected_style_action_btn" : ""
              }`}
              onClick={() => onUpdate(typeObj.class)}
              style={{ ...typeObj.style, flexGrow: "unset", flexShrink: "unset", flexBasis: "unset" }}
            >
              <span style={{ width: "134px" }} className="flex_wrap justify_center">
                {typeObj.name}
                {isSelected && <MaterialIcon name="done" style={{ marginLeft: "8px", fontSize: "20px" }} />}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const Border = ({ value, onUpdate }) => {
  const fontSizeOptions = clasStyles.border;

  return (
    <div className="single_style_wrapper">
      <Label>Border</Label>
      <div className="flex_row align_center">
        {fontSizeOptions.map((fontObj, index) => (
          <div key={index} className={getClassName(value, fontObj.class)} onClick={() => onUpdate(fontObj.class)}>
            <div className="h_75 w_75" style={fontObj.style}></div>
          </div>
        ))}
      </div>
    </div>
  );
};
const BorderRadius = ({ value, onUpdate }) => {
  const fontSizeOptions = clasStyles.borderRadius;

  return (
    <div className="single_style_wrapper">
      <Label>Border Radius</Label>
      <div className="flex_row align_center">
        {fontSizeOptions.map((fontObj, index) => (
          <div key={index} className={getClassName(value, fontObj.class)} onClick={() => onUpdate(fontObj.class)}>
            <div className="h_80 w_75" style={fontObj.style}></div>
          </div>
        ))}
      </div>
    </div>
  );
};

const FontDecoration = ({ value, onUpdate }) => {
  const fontSizeOptions = clasStyles.fontDecoration;

  return (
    <div className="single_style_wrapper">
      <Label>Font Decoration</Label>
      <div className="flex_row align_center align_center">
        {fontSizeOptions.map((fontObj, index) => (
          <div
            key={index}
            style={fontObj.style}
            className={getClassName(value, fontObj.class)}
            onClick={() => onUpdate(fontObj.class)}
          >
            <MaterialIcon name={fontObj.text} className="font_size_medium" />
          </div>
        ))}
      </div>
    </div>
  );
};

// const FontFamily = ({ value, onUpdate }) => {
//   const fontSizeOptions = clasStyles.fontStyle.fontFamily;

//   return (
//     <div className="single_style_wrapper">
//       <Label>Font Family</Label>
//       <div className="flex_row align_center">
//         {fontSizeOptions.map((fontObj, index) => (
//           <div
//             key={index}
//             // style={fontObj.style}
//             className={getClassName(value, fontObj.class)}
//             onClick={() => onUpdate(fontObj.class)}
//           >
//             {fontObj.text}
//           </div>
//         ))}
//       </div>
//     </div>
//   );
// };

const FontSize = ({ value, onUpdate }) => {
  const fontSizeOptions = clasStyles.fontSize;

  return (
    <div className="single_style_wrapper">
      <Label>Font Size</Label>
      <div className="flex_row align_center">
        {fontSizeOptions.map((fontObj, index) => (
          <div
            key={index}
            style={fontObj.style}
            className={getClassName(value, fontObj.class)}
            onClick={() => onUpdate(fontObj.class)}
          >
            T
          </div>
        ))}
      </div>
    </div>
  );
};
const FontWeight = ({ value, onUpdate }) => {
  const fontSizeOptions = clasStyles.fontWeight;

  return (
    <div className="single_style_wrapper">
      <Label>Font Weight</Label>
      <div className="flex_row align_center">
        {fontSizeOptions.map((fontObj, index) => (
          <div
            key={index}
            style={fontObj.style}
            className={getClassName(value, fontObj.class)}
            onClick={() => onUpdate(fontObj.class)}
          >
            A
          </div>
        ))}
      </div>
    </div>
  );
};

const Padding = ({ value, onUpdate }) => {
  const fontSizeOptions = clasStyles.padding;
  const [hovered, setHovered] = useState(null);

  const handleMouseEnter = (index) => {
    setHovered(index);
  };

  const handleMouseLeave = () => {
    setHovered(null);
  };

  return (
    <div className="single_style_wrapper">
      <Label>Padding</Label>
      <div className="flex_row align_center">
        {fontSizeOptions.map((fontObj, index) => (
          <div
            key={index}
            style={{ position: "relative", ...fontObj.style }}
            className={getClassName(value, fontObj.class)}
            onClick={() => onUpdate(fontObj.class)}
            onMouseEnter={() => handleMouseEnter(index)}
            onMouseLeave={handleMouseLeave}
          >
            <div style={fontObj.style}>T</div>

            {hovered === index && <div className="style_tooltip">{fontObj.name}</div>}
          </div>
        ))}
      </div>
    </div>
  );
};

const Margin = ({ value, onUpdate }) => {
  const fontSizeOptions = clasStyles.margin;

  const [hovered, setHovered] = useState(null);

  const handleMouseEnter = (index) => {
    setHovered(index);
  };

  const handleMouseLeave = () => {
    setHovered(null);
  };

  return (
    <div className="single_style_wrapper">
      <Label>Margin</Label>
      <div className="flex_row align_center">
        {fontSizeOptions.map((fontObj, index) => (
          <div
            key={index}
            className={getClassName(value, fontObj.class)}
            onClick={() => onUpdate(fontObj.class)}
            onMouseEnter={() => handleMouseEnter(index)}
            onMouseLeave={handleMouseLeave}
            style={{ position: "relative" }}
          >
            <div style={fontObj.style}>T</div>

            {hovered === index && <div className="style_tooltip">{fontObj.name}</div>}
          </div>
        ))}
      </div>
    </div>
  );
};

const Shadow = ({ value, onUpdate }) => {
  const fontSizeOptions = clasStyles.shadow;

  return (
    <div className="single_style_wrapper">
      <Label>Shadow</Label>
      <div className="flex_row align_center">
        {fontSizeOptions.map((fontObj, index) => (
          <div key={index} className={getClassName(value, fontObj.class)} onClick={() => onUpdate(fontObj.class)}>
            <div className="h_50 w_50" style={fontObj.style}></div>
          </div>
        ))}
      </div>
    </div>
  );
};
const ImageShape = ({ value, onUpdate }) => {
  const fontSizeOptions = clasStyles.imageShape;

  return (
    <div className="single_style_wrapper">
      <Label>Image Shape</Label>
      <div className="flex_row align_center">
        {fontSizeOptions.map((fontObj, index) => (
          <div key={index} className={getClassName(value, fontObj.class)} onClick={() => onUpdate(fontObj.class)}>
            <div style={fontObj.style}></div>
          </div>
        ))}
      </div>
    </div>
  );
};
// Sub-components for each inline style key

const BackgroundColor = ({ value, onUpdate }) => {
  return (
    <div className="single_style_wrapper">
      <Label> Background Color</Label>
      <div className="d_flex">
        <div className="inline_icon_action_preview input_icon_wrapper">
          <Input
            type="color"
            value={value}
            onChange={(e) => onUpdate(e.target.value)}
            containerStyle={{ backgroundColor: value ? value : "black" }}
            inputBoxStyle={{ height: "25px", width: "25px", border: "none" }}
          />
        </div>
        <Input
          inputClassName="border_left_none outline_none"
          value={value}
          onChange={(e) => onUpdate(e.target.value)}
        />
      </div>
    </div>
  );
};
const Color = ({ value, onUpdate }) => {
  return (
    <div className="single_style_wrapper">
      <Label> Color</Label>
      <div className="d_flex">
        <div className="inline_icon_action_preview input_icon_wrapper">
          <Input
            type="color"
            value={value}
            onChange={(e) => onUpdate(e.target.value)}
            containerStyle={{ backgroundColor: value ? value : "black" }}
            inputBoxStyle={{ height: "25px", width: "25px", border: "none" }}
          />
        </div>
        <Input
          inputClassName="border_left_none outline_none"
          value={value}
          onChange={(e) => onUpdate(e.target.value)}
        />
      </div>
    </div>
  );
};
